import axios, { type AxiosRequestConfig } from 'axios'
import { appConfig } from '@/config/config'
import { getFirebaseIdToken } from '@/lib/auth-token'
import { DEV_USER } from '@/lib/devAuth'
import { getViewAs } from '@/lib/view-as'
import { ApiRequestError } from './errors'
import type { ApiErrorBody, ApiSuccess, PaginatedResponse } from './types'

/** Ported from taptalent-account-management-frontend/src/api/axios-client.ts */

/** API Gateway's prod timeout is 30s; stay under it so we own the error message. */
export const API_TIMEOUT_MS = 28_000

export const apiClient = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: API_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
})

apiClient.interceptors.request.use(
  async (config) => {
    // View As: the API swaps in this person's access and refuses anything but reads.
    const viewing = getViewAs()
    if (viewing) config.headers['X-View-As'] = viewing.id
    // Local seeded API only; see lib/devAuth. Compiled out of a production build.
    if (DEV_USER) {
      config.headers['X-Dev-User'] = DEV_USER
      return config
    }
    try {
      const token = await getFirebaseIdToken(false)
      if (token) config.headers.Authorization = `Bearer ${token}`
    } catch {
      // Proceed without a token; the API will answer 401 and the UI will handle it.
    }
    return config
  },
  (error) => Promise.reject(error),
)

apiClient.interceptors.response.use(
  (response) => {
    // The backend can answer 200 with { success: false } for soft failures.
    const body = response.data as ApiSuccess<unknown> | ApiErrorBody | undefined
    if (body && typeof body === 'object' && 'success' in body && body.success === false) {
      throw new ApiRequestError(
        body.error?.message ?? 'Request failed',
        body.error?.code,
        response.status,
        body.error?.details,
        body.error?.requestId ?? response.headers['x-request-id'],
      )
    }
    return response
  },
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        return Promise.reject(
          new ApiRequestError(
            'The server took too long to respond. Try again, or narrow your filters.',
            'TIMEOUT',
          ),
        )
      }

      const status = error.response?.status
      const body = error.response?.data as ApiErrorBody | undefined

      // The backend always sends a typed code and an already-sanitized message; prefer it.
      if (body?.error?.message) {
        return Promise.reject(
          new ApiRequestError(
            body.error.message,
            body.error.code,
            status,
            body.error.details,
            body.error.requestId ?? error.response?.headers?.['x-request-id'],
          ),
        )
      }

      if (status === 401) {
        return Promise.reject(new ApiRequestError('Please sign in again.', 'AUTH_REQUIRED', status))
      }
      if (status === 403) {
        return Promise.reject(
          new ApiRequestError('You do not have permission to do that.', 'ACCESS_DENIED', status),
        )
      }

      return Promise.reject(
        new ApiRequestError(error.message || 'Network error', 'NETWORK', status),
      )
    }

    return Promise.reject(
      error instanceof Error ? error : new ApiRequestError('Unexpected error'),
    )
  },
)

/**
 * Query-string values. Arrays are allowed: axios's default serializer renders them as
 * `ownerIds[]=a&ownerIds[]=b`, which is the shape the API's repeated-value filters expect.
 */
export type QueryParams = Record<string, string | number | boolean | string[] | undefined>

export async function apiGet<T>(
  path: string,
  params?: QueryParams,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.get<ApiSuccess<T>>(path, { ...config, params })
  return res.data.data
}

export async function apiGetPaginated<T>(
  path: string,
  params?: QueryParams,
): Promise<PaginatedResponse<T>> {
  const res = await apiClient.get<ApiSuccess<PaginatedResponse<T>>>(path, { params })
  return res.data.data
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.post<ApiSuccess<T>>(path, body, config)
  return res.data.data
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const res = await apiClient.patch<ApiSuccess<T>>(path, body)
  return res.data.data
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const res = await apiClient.put<ApiSuccess<T>>(path, body)
  return res.data.data
}

/**
 * `data` because DELETE can carry a body, and deleting a work item now asks why.
 *
 * axios needs it under `config.data` rather than as a second argument — the shape differs
 * from post/patch, which is exactly the sort of detail worth wrapping once.
 */
export async function apiDelete<T = void>(path: string, data?: unknown): Promise<T> {
  const res = await apiClient.delete<ApiSuccess<T>>(path, data === undefined ? undefined : { data })
  // Most DELETEs are called for their effect and the caller ignores this. A few say
  // something worth hearing — deleting a subfolder reports how many items it released — and
  // there is no reason to make those go through a second request to find out.
  return res.data?.data
}
